import { Controller } from '@hotwired/stimulus';

const GAP = 4;

const fold = text =>
  text
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/_/g, ' ')
    .toLowerCase();

export default class extends Controller {
  static targets = ['input', 'trigger', 'label', 'hidden'];

  static values = {
    items: Array,
    selected: Array,
    name: String,
    placeholder: String,
    filterText: { type: String, default: 'Filter…' },
    emptyText: { type: String, default: 'No matches' },
    autocomplete: Boolean,
    filter: Boolean,
    multiple: Boolean,
    maxHeight: { type: Number, default: 288 },
    limit: { type: Number, default: 50 },
  };

  connect() {
    this.index = -1;
    this.matches = [];
    this.panel = null;
    this.onOutside = this.onOutside.bind(this);
    this.onMove = this.place.bind(this);
    this.ready = true;
  }

  disconnect() {
    this.close();
  }

  get anchor() {
    return this.autocompleteValue ? this.inputTarget : this.triggerTarget;
  }

  get isOpen() {
    return this.panel !== null;
  }

  toggle(event) {
    event?.preventDefault();

    this.isOpen ? this.close() : this.open();
  }

  open() {
    if (!this.isOpen) this.mount();

    this.refresh();
  }

  close() {
    if (!this.isOpen) return;

    this.panel.remove();
    this.panel = null;
    this.search = null;
    this.index = -1;
    this.anchor.setAttribute('aria-expanded', 'false');

    document.removeEventListener('mousedown', this.onOutside, true);
    window.removeEventListener('scroll', this.onMove, true);
    window.removeEventListener('resize', this.onMove);
  }

  typed() {
    this.open();
  }

  blurred() {
    if (this.autocompleteValue) this.close();
  }

  keydown(event) {
    if (!this.isOpen) {
      if (['ArrowDown', 'Enter', ' '].includes(event.key) && !this.autocompleteValue) {
        event.preventDefault();
        this.open();
      }

      if (event.key === 'ArrowDown' && this.autocompleteValue) this.open();

      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();

      if (!this.matches.length) return;

      const step = event.key === 'ArrowDown' ? 1 : -1;

      this.index = (this.index + step + this.matches.length) % this.matches.length;
      this.render();

      return;
    }

    if ((event.key === 'Enter' || event.key === 'Tab') && this.index >= 0) {
      event.preventDefault();
      this.choose(this.matches[this.index]);

      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      this.anchor.focus();
    }
  }

  mount() {
    this.panel = document.createElement('div');
    this.panel.className =
      'dd-panel';
    this.panel.style.maxHeight = `${this.maxHeightValue}px`;

    if (this.filterValue && !this.autocompleteValue) {
      this.search = document.createElement('input');
      this.search.type = 'text';
      this.search.placeholder = this.filterTextValue;
      this.search.autocomplete = 'off';
      this.search.setAttribute('aria-label', 'Filter options');
      this.search.className =
        'dd-search';
      this.search.addEventListener('input', () => this.refresh());
      this.search.addEventListener('keydown', event => this.keydown(event));
      this.panel.appendChild(this.search);
    }

    this.list = document.createElement('ul');
    this.list.setAttribute('role', 'listbox');
    this.list.setAttribute('aria-multiselectable', String(this.multipleValue));
    this.list.className = 'dd-list';
    this.panel.appendChild(this.list);
    this.panel.appendChild(this.footer());

    (this.element.closest('dialog') ?? document.body).appendChild(this.panel);
    this.anchor.setAttribute('aria-expanded', 'true');
    this.place();

    document.addEventListener('mousedown', this.onOutside, true);
    window.addEventListener('scroll', this.onMove, true);
    window.addEventListener('resize', this.onMove);

    this.search?.focus();
  }

  footer() {
    const footer = document.createElement('div');
    const keys = this.autocompleteValue
      ? 'Tab to complete'
      : this.multipleValue
        ? 'Enter to toggle'
        : 'Enter to select';

    footer.className = 'dd-foot';
    footer.textContent = `${keys} · ↑↓ to choose`;
    footer.setAttribute('aria-hidden', 'true');

    return footer;
  }

  place() {
    if (!this.isOpen) return;

    const rect = this.anchor.getBoundingClientRect();
    const width = Math.max(rect.width, 192);
    const height = Math.min(this.panel.scrollHeight, this.maxHeightValue);
    const below = rect.bottom + GAP + height <= window.innerHeight;

    this.panel.style.width = `${width}px`;
    this.panel.style.left = `${Math.min(Math.max(GAP, rect.left), window.innerWidth - width - GAP)}px`;
    this.panel.style.top = `${below ? rect.bottom + GAP : Math.max(GAP, rect.top - GAP - height)}px`;
  }

  onOutside(event) {
    if (this.panel.contains(event.target) || this.element.contains(event.target)) return;

    this.close();
  }

  query() {
    const source = this.autocompleteValue ? this.inputTarget.value : this.search?.value;

    return fold(source || '').trim();
  }

  refresh() {
    const query = this.query();
    const label = item => fold(item.label);
    const items = this.itemsValue;
    const starts = items.filter(item => label(item).startsWith(query));
    const words = items.filter(item => !starts.includes(item) && [' ', '/'].some(mark => label(item).includes(mark + query)));
    const contains = items.filter(item => !starts.includes(item) && !words.includes(item) && label(item).includes(query));
    let matches = [...starts, ...words, ...contains];

    if (this.autocompleteValue) matches = matches.filter(item => label(item) !== query);

    this.matches = matches.slice(0, this.limitValue);
    this.index = this.startIndex(query);

    if (this.autocompleteValue && !this.matches.length) {
      this.close();

      return;
    }

    this.render();
  }

  render() {
    this.list.replaceChildren();

    if (!this.matches.length) {
      const empty = document.createElement('li');

      empty.className = 'dd-empty';
      empty.textContent = this.emptyTextValue;
      this.list.appendChild(empty);
    }

    this.matches.forEach((item, position) => this.list.appendChild(this.option(item, position)));

    this.list.children[this.index]?.scrollIntoView({ block: 'nearest' });
    this.place();
  }

  option(item, position) {
    const option = document.createElement('li');
    const picked = this.selectedValue.includes(item.value);

    option.setAttribute('role', 'option');
    option.setAttribute('aria-selected', String(position === this.index));
    option.className =
      'dd-option';

    if (this.multipleValue) {
      const box = document.createElement('span');

      box.className = picked
        ? 'dd-box on'
        : 'dd-box';
      box.textContent = picked ? '✓' : '';
      option.appendChild(box);
    }

    const text = document.createElement('span');

    text.className = 'dd-text';
    text.textContent = item.label;
    option.appendChild(text);

    if (!this.multipleValue && !this.autocompleteValue && picked) {
      const check = document.createElement('span');

      check.className = 'dd-check';
      check.textContent = '✓';
      option.appendChild(check);
    }

    option.addEventListener('mouseenter', () => this.highlight(position));

    option.addEventListener('mousedown', event => {
      event.preventDefault();
      this.choose(item);
    });

    return option;
  }

  // Autocomplete and a typed filter start on the best match (Tab/Enter take
  // it); a plain select opens on the picked option, or on nothing.
  startIndex(query) {
    if (!this.matches.length) return -1;
    if (this.autocompleteValue || query) return 0;

    return this.matches.findIndex(item => this.selectedValue.includes(item.value));
  }

  highlight(position) {
    this.index = position;

    [...this.list.querySelectorAll('[role=option]')].forEach((option, at) => {
      option.setAttribute('aria-selected', String(at === position));
    });
  }

  choose(item) {
    if (this.autocompleteValue) {
      this.inputTarget.value = item.value;
      this.close();
      this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }));

      return;
    }

    if (this.multipleValue) {
      const selected = new Set(this.selectedValue);

      selected.has(item.value) ? selected.delete(item.value) : selected.add(item.value);
      this.selectedValue = this.itemsValue.map(each => each.value).filter(value => selected.has(value));
      this.index = this.matches.indexOf(item);
      this.render();

      return;
    }

    this.selectedValue = [item.value];
    this.close();
    this.triggerTarget.focus();
  }

  selectedValueChanged() {
    if (this.autocompleteValue || !this.hasHiddenTarget) return;

    const name = this.multipleValue ? `${this.nameValue}[]` : this.nameValue;
    const values = this.selectedValue.length || this.multipleValue ? this.selectedValue : [''];

    this.hiddenTarget.replaceChildren(
      ...values.map(value => {
        const input = document.createElement('input');

        input.type = 'hidden';
        input.name = name;
        input.value = value;

        return input;
      })
    );

    this.labelTarget.textContent = this.summary();
    this.labelTarget.classList.toggle('muted', this.selectedValue.length === 0);

    if (this.ready) this.hiddenTarget.dispatchEvent(new Event('change', { bubbles: true }));
  }

  summary() {
    const labels = this.itemsValue.filter(item => this.selectedValue.includes(item.value)).map(item => item.label);

    if (!labels.length) return this.placeholderValue;
    if (labels.length <= 2) return labels.join(', ');

    return `${labels.length} selected`;
  }
}
